/**
 * Trend Data Hook
 *
 * TanStack Query hook for fetching and transforming pain trend data.
 * Combines daily records and events into chart-ready format.
 */

import { useQuery } from "@tanstack/react-query";
import {
  createApiClient,
  ApiClientError,
  type DailyRecordResponse,
  type EventResponse,
  type UserMedicationResponse,
  type WeatherDailyPoint,
} from "@floline/api-client";
import { API_URL } from "@/env";
import {
  buildTrendDataPoints,
  buildEventMarkers,
  buildEventSpans,
  buildMedicationSpans,
  filterChartOnlySpans,
  calculateTrendStatistics,
  calculateLocationFrequency,
  getDateRangeStart,
  formatLocalDate,
  generateDateRange,
  type TrendDataPoint,
  type ChartEventMarker,
  type ChartEventSpan,
  type DateRangeOption,
  type LocationFrequency,
} from "@/components/trends/pain-trend-utils";
import { groupEventsByDate, type EventsByDate } from "@/components/timeline/timeline-event-utils";

// ============================================================================
// Query Keys
// ============================================================================

/**
 * Query key factory for trend data
 */
export const trendKeys = {
  all: ["trends"] as const,
  records: (startDate: string, endDate: string) =>
    [...trendKeys.all, "records", startDate, endDate] as const,
  events: (startDate: string, endDate: string) =>
    [...trendKeys.all, "events", startDate, endDate] as const,
  weather: (startDate: string, endDate: string) =>
    [...trendKeys.all, "weather", startDate, endDate] as const,
  data: (dateRange: DateRangeOption) =>
    [...trendKeys.all, "data", dateRange] as const,
};

// ============================================================================
// API Client
// ============================================================================

const apiClient = createApiClient(
  API_URL
);

// ============================================================================
// Fetch Functions
// ============================================================================

/**
 * Fetch daily records for a date range
 */
async function fetchRecordsForRange(
  startDate: string,
  endDate: string
): Promise<DailyRecordResponse[]> {
  // Use pagination so "all" truly means all available history.
  // (A hard limit like 1500 caps at ~4 years of daily entries.)
  const limit = 1500;
  const all: DailyRecordResponse[] = [];
  let offset = 0;
  // Safety guard in case server meta is wrong
  const maxPages = 20;

  for (let page = 0; page < maxPages; page++) {
    const response = await apiClient.listDailyRecords({
      startDate,
      endDate,
      limit,
      offset,
    });

    all.push(...response.data);
    offset += response.data.length;

    const total = response.meta?.total ?? all.length;
    if (all.length >= total) break;
    if (response.data.length < limit) break;
  }

  return all;
}

/**
 * Fetch events for a date range
 */
async function fetchEventsForRange(
  startDate: string,
  endDate: string
): Promise<EventResponse[]> {
  const limit = 200;
  const all: EventResponse[] = [];
  let offset = 0;
  const maxPages = 20;

  for (let page = 0; page < maxPages; page++) {
    const response = await apiClient.listEvents({
      startDate,
      endDate,
      limit,
      offset,
    });

    all.push(...response.data);
    offset += response.data.length;

    const total = response.meta?.total ?? all.length;
    if (all.length >= total) break;
    if (response.data.length < limit) break;
  }

  return all;
}

function addDaysIso(dateString: string, deltaDays: number): string {
  const d = new Date(`${dateString}T12:00:00`);
  d.setDate(d.getDate() + deltaDays);
  return formatLocalDate(d);
}

function mergeIsoRanges(
  ranges: Array<{ startDate: string; endDate: string }>
): Array<{ startDate: string; endDate: string }> {
  const sorted = [...ranges]
    .filter((r) => r.startDate <= r.endDate)
    .sort((a, b) => a.startDate.localeCompare(b.startDate));

  const merged: Array<{ startDate: string; endDate: string }> = [];
  for (const r of sorted) {
    const prev = merged[merged.length - 1];
    if (!prev) {
      merged.push({ ...r });
      continue;
    }

    // Merge overlapping or adjacent ranges
    const prevEndPlus1 = addDaysIso(prev.endDate, 1);
    if (r.startDate <= prevEndPlus1) {
      if (r.endDate > prev.endDate) prev.endDate = r.endDate;
      continue;
    }

    merged.push({ ...r });
  }

  return merged;
}

function minIsoDate(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return a <= b ? a : b;
}

function getEarliestEntryDate(input: {
  records: DailyRecordResponse[];
  events: EventResponse[];
  medications: UserMedicationResponse[];
}): string | null {
  let earliest: string | null = null;

  for (const r of input.records) {
    earliest = minIsoDate(earliest, r.recordDate);
  }

  for (const e of input.events) {
    earliest = minIsoDate(earliest, e.startDate);
  }

  for (const m of input.medications) {
    earliest = minIsoDate(earliest, m.startDate);
  }

  return earliest;
}

/**
 * Fetch all user medications (active and stopped) for medication duration spans
 */
async function fetchAllMedications(): Promise<UserMedicationResponse[]> {
  const response = await apiClient.listMedications({ status: "all" });
  return response.data;
}

/**
 * Fetch weather daily data for a date range.
 *
 * Returns undefined when no locations are configured or the API returns
 * an empty array (graceful degradation — weather is optional).
 * Uses the same startDate/endDate as the trend data range so chart
 * alignment is automatic.
 */
async function fetchWeatherForRange(
  startDate: string,
  endDate: string
): Promise<WeatherDailyPoint[] | undefined> {
  try {
    const response = await apiClient.listWeatherDaily({ startDate, endDate });
    // Return undefined when no data (e.g. no locations configured)
    if (!response.data || response.data.length === 0) {
      return undefined;
    }
    return response.data;
  } catch (error) {
    // Weather is non-critical; degrade gracefully on errors
    // (e.g. 404 if no locations, 500 from Open-Meteo outage)
    if (error instanceof ApiClientError) {
      console.warn(
        `[useTrendData] Weather fetch failed (status ${error.status}): ${error.message}`
      );
    } else {
      console.warn("[useTrendData] Weather fetch failed:", error);
    }
    return undefined;
  }
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Trend data result
 */
export interface TrendDataResult {
  /** Trend data points for chart */
  dataPoints: TrendDataPoint[];
  /** Event markers for chart overlay */
  eventMarkers: ChartEventMarker[];
  /** Events grouped by date */
  eventsByDate: EventsByDate;
  /** Event spans for timeline overlays (all medications, regardless of showInTrends) */
  eventSpans: ChartEventSpan[];
  /** Event spans for chart overlays only (medications filtered by showInTrends) */
  chartEventSpans: ChartEventSpan[];
  /** Summary statistics */
  statistics: ReturnType<typeof calculateTrendStatistics>;
  /** Location frequency analysis */
  locationFrequency: LocationFrequency[];
  /** Raw daily records */
  records: DailyRecordResponse[];
  /**
   * Analysis records used for impact computations.
   * Includes the displayed range plus any extra windows needed to compute
   * true before/after around medication lifecycle boundaries.
   */
  analysisRecords: DailyRecordResponse[];
  /** Events included in trend analytics/overlays */
  events: EventResponse[];
  /**
   * Weather daily points aligned to the trend date range.
   * Undefined when no user locations are configured or weather API
   * returns empty/errors (weather is optional context).
   */
  weatherDaily?: WeatherDailyPoint[];
  /** Start date of range */
  startDate: string;
  /** End date of range */
  endDate: string;
}

/**
 * Hook for fetching pain trend data
 *
 * Fetches daily records and events for the selected date range,
 * transforms them into chart-ready format with statistics.
 *
 * @param dateRange - Selected date range (7d, 30d, 90d, etc.)
 * @returns Trend data, loading state, and error handling
 *
 * @example
 * ```tsx
 * function TrendsPage() {
 *   const [dateRange, setDateRange] = useState<DateRangeOption>("30d");
 *   const { data, isLoading, isError } = useTrendData(dateRange);
 *
 *   if (isLoading) return <Spinner />;
 *   return <PainTrendChart data={data.dataPoints} eventMarkers={data.eventMarkers} />;
 * }
 * ```
 */
export function useTrendData(dateRange: DateRangeOption) {
  // Calculate date range
  const today = new Date();
  const endDate = formatLocalDate(today);
  const startDate = getDateRangeStart(dateRange, today);

  // Weather is fetched through today + 6 so that forecast-temperature data
  // (Open-Meteo supplies 7-day forecasts) is available for the flare
  // forecast weather adjustment.  Records/events still end at today.
  const weatherEndDate = (() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 6);
    return formatLocalDate(d);
  })();

  const query = useQuery({
    queryKey: trendKeys.data(dateRange),
    queryFn: async (): Promise<TrendDataResult> => {
      // Fetch records, events, and medications first.
      // For "all", we derive the true start date from the user's earliest entry
      // (record/event/medication) so charts don't include a large empty tail.
      const [records, events, medications] = await Promise.all([
        fetchRecordsForRange(startDate, endDate),
        fetchEventsForRange(startDate, endDate),
        fetchAllMedications(),
      ]);

      // One-off medication taken events can be very high volume and noisy for
      // trend overlays/cards. Keep them in timeline views, but exclude them
      // from trends event chips/impact analytics.
      const trendEvents = events.filter((e) => e.eventType !== "medication_taken");

      const effectiveStartDate =
        dateRange === "all"
          ? (getEarliestEntryDate({ records, events: trendEvents, medications }) ?? endDate)
          : startDate;

      // Ensure we can open medication-linked event dialogs even when the
      // medication_start event is outside the displayed range.
      const eventsForMedicationLinking = [...trendEvents];
      const medsMissingLinkedEvent = medications.filter(
        (m) => !trendEvents.some((e) => e.relatedMedicationId === m.id)
      );
      if (medsMissingLinkedEvent.length > 0) {
        const extra = await Promise.all(
          medsMissingLinkedEvent.map(async (m) => {
            try {
              const response = await apiClient.listEvents({
                relatedMedicationId: m.id,
                limit: 50,
                offset: 0,
              });
              return response.data;
            } catch {
              return [];
            }
          })
        );
        for (const rows of extra) {
          eventsForMedicationLinking.push(...rows);
        }
      }

      // Fetch additional daily records needed for impact analysis.
      // The displayed chart remains clipped to effectiveStartDate..endDate.
      const extraRanges: Array<{ startDate: string; endDate: string }> = [];

      if (dateRange !== "all") {
        // Window-scoped before/after calculations need a small buffer outside the
        // displayed range (e.g. 7-day lookback from effectiveStartDate).
        extraRanges.push({
          startDate: addDaysIso(effectiveStartDate, -7),
          endDate: addDaysIso(effectiveStartDate, -1),
        });

        for (const med of medications) {
          const medEnd = med.endDate ?? endDate;
          const overlapsDisplay = med.startDate <= endDate && medEnd >= effectiveStartDate;
          if (!overlapsDisplay) continue;

          // True "before" around medication start
          if (med.startDate < effectiveStartDate) {
            extraRanges.push({
              startDate: addDaysIso(med.startDate, -7),
              endDate: addDaysIso(med.startDate, -1),
            });
          }

          // True "after" around medication end (only if ended before today)
          if (med.endDate && med.endDate < endDate) {
            const afterStart = addDaysIso(med.endDate, 1);
            const afterEndRaw = addDaysIso(med.endDate, 7);
            const afterEnd = afterEndRaw > endDate ? endDate : afterEndRaw;
            if (afterStart <= afterEnd) {
              extraRanges.push({ startDate: afterStart, endDate: afterEnd });
            }
          }
        }
      }

      const mergedRanges = mergeIsoRanges(extraRanges);
      const extraRecordsBatches = await Promise.all(
        mergedRanges.map((r) => fetchRecordsForRange(r.startDate, r.endDate))
      );
      const analysisRecordByDate = new Map<string, DailyRecordResponse>();
      for (const r of records) analysisRecordByDate.set(r.recordDate, r);
      for (const batch of extraRecordsBatches) {
        for (const r of batch) analysisRecordByDate.set(r.recordDate, r);
      }
      const analysisRecords = Array.from(analysisRecordByDate.values()).sort((a, b) =>
        a.recordDate.localeCompare(b.recordDate)
      );

      // Weather range extends 6 days beyond today so forecasted temps are
      // available for the flare forecast weather nudge.
      const weatherDaily = await fetchWeatherForRange(
        effectiveStartDate,
        weatherEndDate
      );

      // Group events by date
      const eventsByDate = groupEventsByDate(trendEvents);

      // Build trend data points
      const dataPoints = buildTrendDataPoints(records, effectiveStartDate, endDate);

      // Generate date range for markers
      const dateRangeArray = generateDateRange(effectiveStartDate, endDate);

      // Build event markers
      const eventMarkers = buildEventMarkers(eventsByDate, dateRangeArray);

      // Build event spans (non-medication) + medication duration spans from user_medications
      const nonMedEventSpans = buildEventSpans(trendEvents, effectiveStartDate, endDate);
      const medicationSpans = buildMedicationSpans(
        medications,
        effectiveStartDate,
        endDate,
        eventsForMedicationLinking
      );
      const eventSpans = [...nonMedEventSpans, ...medicationSpans].sort(
        (a, b) => {
          const startCompare = a.startDate.localeCompare(b.startDate);
          if (startCompare !== 0) return startCompare;
          return a.endDate.localeCompare(b.endDate);
        },
      );

      // Chart-only spans: filter medications by showInTrends flag.
      // Non-medication events always pass through.
      const chartVisibleMedIds = new Set(
        medications.filter((m) => m.showInTrends).map((m) => m.id),
      );
      const chartEventSpans = filterChartOnlySpans(eventSpans, chartVisibleMedIds);

      // Calculate statistics
      const statistics = calculateTrendStatistics(dataPoints);

      // Calculate location frequency
      const locationFrequency = calculateLocationFrequency(records);

      return {
        dataPoints,
        eventMarkers,
        eventSpans,
        chartEventSpans,
        eventsByDate,
        statistics,
        locationFrequency,
        records,
        analysisRecords,
        events: trendEvents,
        weatherDaily,
        startDate: effectiveStartDate,
        endDate,
      };
    },
    // Cache for 1 minute
    staleTime: 60 * 1000,
    // Retry on errors (except auth errors)
    retry: (failureCount: number, error: Error) => {
      if (error instanceof ApiClientError && error.status === 401) {
        return false;
      }
      return failureCount < 3;
    },
    // Keep previous range visible while loading a new range
    placeholderData: (previousData) => previousData,
    refetchOnMount: true,
  });

  return {
    /** Trend data result */
    data: query.data ?? null,
    /** Whether data is being fetched */
    isLoading: query.isLoading && !query.data,
    /** Whether a background refetch is in progress */
    isFetching: query.isFetching,
    /** Whether fetch failed */
    isError: query.isError,
    /** Error details */
    error: query.error,
    /** Refetch data */
    refetch: query.refetch,
    /** Date range being displayed */
    dateRange,
    /** Start date */
    startDate: query.data?.startDate ?? startDate,
    /** End date */
    endDate,
  };
}
